import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

const linkBase = "text-sm tracking-wide transition-colors px-3 py-2";
const inactive = "text-[#a3a8a4] hover:text-[#fdfbf7]";
const active = "text-[#d4af37]";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const isAdmin = user && user.role === "admin";

  return (
    <header className="sticky top-0 z-30 glass border-b border-[rgba(253,251,247,0.08)]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <span className="font-display text-2xl tracking-tight">
            <span className="gold-text">Noctalis</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {!isAdmin && (
            <>
              {user && user.role === "client" && (
                <NavLink to="/dashboard" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-dashboard">Tableau de bord</NavLink>
              )}
              <NavLink to="/produits" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-produits">Produits</NavLink>
              {user && user.role === "client" && (
                <NavLink to="/recompenses" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-recompenses">Récompenses</NavLink>
              )}
              <NavLink to="/contact" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-contact">Contact</NavLink>
              {user && user.role === "client" && (
                <NavLink to="/profil" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-profil">Profil</NavLink>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <NavLink to="/admin" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-admin">Tableau Admin</NavLink>
              <NavLink to="/admin/produits" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-admin-produits">Produits</NavLink>
              <NavLink to="/admin/recompenses" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-admin-recompenses">Récompenses</NavLink>
              <NavLink to="/admin/clients" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-admin-clients">Clients</NavLink>
              <NavLink to="/admin/messages" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-admin-messages">Messages</NavLink>
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <div className="text-sm text-[#a3a8a4]" data-testid="navbar-username">
                <span className="text-[#fdfbf7]">{user.firstName}</span>
                {user.role === "client" && (
                  <span className="ml-3 gold-text font-medium" data-testid="navbar-points">{user.points} pts</span>
                )}
              </div>
              <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-[#a3a8a4] hover:text-[#fdfbf7] px-3 py-2" data-testid="logout-btn">
                <LogOut size={16}/> Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-[#a3a8a4] hover:text-[#fdfbf7] px-3 py-2" data-testid="login-link">Connexion</Link>
              <Link to="/register" className="text-sm bg-[#d4af37] text-black px-5 py-2.5 font-medium tracking-widest uppercase hover:bg-[#e6c27a] transition-colors btn-shimmer" data-testid="register-link">S'inscrire</Link>
            </>
          )}
        </div>

        <button className="md:hidden text-[#fdfbf7]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X/> : <Menu/>}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[rgba(253,251,247,0.08)] px-6 py-4 flex flex-col gap-2 glass" data-testid="mobile-menu">
          {!isAdmin && (
            <>
              {user && user.role === "client" && (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Tableau de bord</Link>
              )}
              <Link to="/produits" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Produits</Link>
              {user && user.role === "client" && (
                <Link to="/recompenses" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Récompenses</Link>
              )}
              <Link to="/contact" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Contact</Link>
              {user && user.role === "client" && (
                <Link to="/profil" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Profil</Link>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Tableau Admin</Link>
              <Link to="/admin/produits" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Produits</Link>
              <Link to="/admin/recompenses" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Récompenses</Link>
              <Link to="/admin/clients" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Clients</Link>
              <Link to="/admin/messages" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Messages</Link>
            </>
          )}
          {user ? (
            <button onClick={handleLogout} className="py-2 text-left text-[#a3a8a4]">Déconnexion</button>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-[#fdfbf7]">Connexion</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="py-2 text-[#d4af37]">S'inscrire</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
